import { useEffect } from 'react';
// eslint-disable-next-line no-restricted-imports -- PointReceiveToast가 visible 상태를 사용하는 custom renderer라 기존 런타임을 유지한다.
import { toast } from 'react-hot-toast';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { logger } from '@zep/core-logger';
import { trackEvent } from '@zep/logger/tracking';
import { useGetJoinedClassrooms } from '@/api/gen/classroom/classroom.gen';
import { useUser } from '@/lib/auth';
import { ApiTokenStorage } from '@/module/auth/utils/ApiTokenStorage';
import { PointReceiveToast } from '../components/PointReceiveToast';
interface PointGrantedEvent {
  quizUserId: string;
  classGroupId?: string;
  amount: number;
  currentBalance: number;
  ledgerEntryId: string;
  reasonType: 'CATEGORY' | 'CUSTOM';
  reasonDetail: string;
  createdAt?: string | null;
}
const POINT_EVENTS_URL = `${process.env.NEXT_PUBLIC_SERVER_BASEURL}/api/points/events`;

/**
 * 공통 성공 토스트 기본값(2초)은 이 알림엔 짧다 — 학생은 퀴즈 화면을 보고 있다가
 * 뒤늦게 알아채므로 인지에만 1~2초가 먼저 깎인다("엄청 빨리 사라져서 집중해서 봐야대여").
 * 전역 기본값을 바꾸면 앱 전체 성공 토스트가 같이 길어지므로 이 호출부에서만 늘린다.
 */
const POINT_RECEIVE_TOAST_DURATION_MS = 5000;

/** 재연결 백오프. 성공적으로 열리면 초기값으로 되돌린다. */
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

/**
 * 연속 실패가 이 횟수에 도달하면 한 번만 리포트한다.
 * 매 실패를 올리면 재연결 루프가 그대로 에러 모니터링 노이즈가 된다.
 */
const REPORT_AFTER_CONSECUTIVE_FAILURES = 5;

/**
 * 서버가 응답으로 연속 거절하면 이 횟수에서 구독을 포기한다(백오프와 합쳐 약 2분 반).
 * 만료 토큰이 406 으로 덮여 오면 401/403 판정을 통과해 세션 내내 재연결하기 때문이다.
 * 네트워크 단절(응답 없음)은 세지 않는다 — 복구되면 다시 붙어야 한다.
 */
const MAX_RECONNECT_ATTEMPTS = 10;

/**
 * 재시도해도 소용없는 종료 사유. 이 에러만 구독을 완전히 끝낸다.
 *
 * 현재는 인증 실패(401/403)뿐이다. 토큰이 죽은 상태에서 1초마다 다시 붙어봐야
 * 계속 401 이고, 로그인 유도는 이 훅이 아니라 전역 에러 핸들러(useApiError →
 * createQueryCache/createMutationCache)가 담당한다. 여기서는 조용히 구독만 끊고,
 * 학생이 다음에 실제 API 를 호출하는 순간 기존 경로로 로그인 화면에 도달한다.
 */
class PointSseFatalError extends Error {}

/** 서버가 응답은 했지만 스트림을 열어주지 않았다. 재시도 상한은 이것만 센다. */
class PointSseRejectedError extends Error {}
function isPlayPathname() {
  return window.location.pathname.includes('/play/');
}

/**
 * play 진입 시에만 구독을 뒤로 미룬다.
 *
 * SSE 는 세션 내내 커넥션 하나를 물고 있어서, play 초기 API 버스트와 같은 시점에
 * 붙으면 호스트당 커넥션 슬롯을 한 칸 뺏는다(HTTP/1.1 이면 6 → 5). 로딩이 매번
 * 조금씩 느려지는 원인. 포인트 알림은 실시간성이 몇 초 단위로 급하지 않으므로
 * 초기 로딩이 끝날 때까지만 비켜준다.
 *
 * 이 지연은 그대로 알림 누락 창이다(연결이 없는 동안 온 지급은 못 받는다).
 * 그래서 버스트를 피할 만큼만 짧게 잡는다.
 */
const PLAY_SSE_START_DELAY_MS = 3_000;
export function usePointReceiveEvents() {
  const {
    user
  } = useUser();
  const router = useRouter();
  const queryClient = useQueryClient();
  const quizUserId = user?.quizUserId;
  /**
   * 토큰을 컨텍스트에서 읽고 의존성에도 넣는다. localStorage 만 읽으면 effect 실행
   * 시점의 값으로 고정돼, 계정은 그대로인 채 토큰만 갱신되는 경우(setUserSession)
   * 만료된 토큰으로 붙은 연결이 그대로 남는다. 컨텍스트가 비어 있는 초기 프레임을
   * 대비해 저장소를 폴백으로 둔다.
   */
  const apiToken = user?.token || ApiTokenStorage.get();
  const isStudent = user?.type?.toLowerCase() === 'student';
  const isPlayRoute = router.asPath.includes('/play/');

  /**
   * 포인트는 클래스 소속 학생에게만 지급되므로, 가입한 클래스가 없으면 이 연결은
   * 아무 이벤트도 받지 못한 채 세션 내내 열려만 있다. 이 훅은 `_app` 에 마운트돼
   * 있고 `openWhenHidden: true` 라 백그라운드 탭에서도 유지되고, 보드 상세에서는
   * `useBoardSSE` 와 겹쳐 한 탭에 장기 연결이 둘이 된다.
   *
   * 목록 자체는 클래스 화면들이 이미 쓰는 쿼리라 캐시를 재사용하고, 가입/탈퇴 시
   * `invalidateClassroomListQueries` 가 이 키를 무효화하므로 가입 직후 연결이 열린다.
   */
  const {
    data: joinedClassrooms
  } = useGetJoinedClassrooms(undefined, {
    query: {
      enabled: isStudent && !!apiToken,
      // 이 훅은 `_app` 에서 전 페이지에 마운트된다. 쿼리 기본값(throwOnError: true)을
      // 그대로 두면 /classrooms/joined 의 일시적인 500 하나가 전역 ErrorBoundary 를
      // 띄워 앱 전체를 오류 화면으로 바꾼다. 이건 알림 연결 여부만 정하는 부가
      // 조회이므로, 실패하면 SSE 만 열지 않고 화면은 그대로 둔다.
      throwOnError: false,
      meta: {
        ignoreError: true
      }
    }
  });
  const hasJoinedClassroom = (joinedClassrooms?.data.length ?? 0) > 0;
  useEffect(() => {
    if (!isStudent) return;
    // 가입한 클래스가 없어도 **플레이 중이면** 연결한다. 젤리가 클래스 안에서만
    // 오가던 시절의 게이트라, 그대로 두면 플레이에서 받은 학생에게 토스트가 안 뜬다.
    // 지금은 학급 밖 참가자도 받는다(PRD §3).
    if (!hasJoinedClassroom && !isPlayRoute) return;
    if (!apiToken) return;
    const abortController = new AbortController();
    let consecutiveFailures = 0;
    let consecutiveRejections = 0;

    /**
     * 재연결 간격. `onerror` 가 이 값을 반환하면 라이브러리가 그만큼 뒤에 다시 붙는다.
     *
     * `onerror` 안에서 abort 를 부르면 안 된다 — 라이브러리는 abort 여부를 onerror
     * *호출 전에* 판정하므로, 뒤늦게 abort 해도 재연결 타이머는 그대로 걸리고
     * 그 재연결은 input signal 을 더는 보고 있지 않아 언마운트로도 못 끊는다.
     * 끝내려면 반환이 아니라 throw 여야 한다.
     */
    const nextReconnectDelay = () => Math.min(RECONNECT_BASE_MS * 2 ** (consecutiveFailures - 1), RECONNECT_MAX_MS);
    const connect = () => void fetchEventSource(POINT_EVENTS_URL, {
      headers: {
        Authorization: `Bearer ${apiToken}`
      },
      signal: abortController.signal,
      openWhenHidden: true,
      onopen: async response => {
        const contentType = response.headers.get('content-type');
        if (response.ok && contentType?.toLowerCase().includes('text/event-stream')) {
          consecutiveFailures = 0;
          consecutiveRejections = 0;
          return;
        }
        if (response.status === 401 || response.status === 403) {
          throw new PointSseFatalError(`Point SSE unauthorized: status=${response.status}`);
        }

        // 그 외(5xx, 프록시 오설정으로 인한 HTML/JSON 응답 등)는 일시적일 수 있어
        // 재연결 대상으로 넘긴다. 리포트는 onerror 가 임계치에서 한 번만 한다.
        throw new PointSseRejectedError(`Point SSE failed to open: status=${response.status} contentType=${contentType}`);
      },
      onmessage: event => {
        if (event.event !== 'point-granted' || !event.data) return;
        let payload: PointGrantedEvent;
        try {
          payload = JSON.parse(event.data) as PointGrantedEvent;
        } catch {
          // 여기서 throw 하면 스트림 전체가 끊긴다. 깨진 이벤트 하나는 버리고 계속 듣는다.
          return;
        }

        // 서버는 유저별 emitter 로만 보내지만, pub/sub fan-out 이 어긋나면 남의 지급이
        // 내 화면에 뜬다. 화면에 그리기 전에 대상만 한 번 확인한다.
        if (quizUserId && payload.quizUserId !== quizUserId) return;

        // 학생이 '받았다'를 인지하는 유일한 순간이라 토스트를 띄우기 직전에 찍는다.
        // 지급 시각과의 차이를 함께 남겨 SSE 지연·미연결 구간을 관측한다.
        const grantedAt = payload.createdAt ? Date.parse(payload.createdAt) : Number.NaN;
        trackEvent('quiz_point_receive_view', {
          amount: payload.amount,
          current_jelly_balance: payload.currentBalance,
          classroom_id: payload.classGroupId ?? '',
          reason_type: payload.reasonType,
          place: isPlayPathname() ? 'play' : 'other',
          is_tab_visible: document.visibilityState === 'visible',
          ...(Number.isFinite(grantedAt) && {
            grant_to_view_ms: Date.now() - grantedAt
          })
        });
        toast.custom(toastInstance => <PointReceiveToast amount={payload.amount} reason={payload.reasonDetail} isVisible={toastInstance.visible} />, {
          position: 'top-center',
          duration: POINT_RECEIVE_TOAST_DURATION_MS
        });
        void queryClient.invalidateQueries({
          queryKey: ['my-points']
        });
      },
      /**
       * 스트림이 에러 없이 끝난 경우 — 서버 emitter 타임아웃(3시간), 파드 드레인 등.
       * 라이브러리는 이때 onerror 를 부르지 않고 그냥 종료하므로, throw 로 재연결
       * 경로에 합류시킨다. 그러지 않으면 학생은 남은 세션 내내 알림을 못 받고,
       * 끊긴 사실조차 아무 데도 남지 않는다.
       */
      onclose: () => {
        throw new Error('Point SSE closed by server');
      },
      onerror: err => {
        // 인증 실패는 재시도해도 같은 결과다. throw 하면 라이브러리가 정리하고 끝낸다.
        if (err instanceof PointSseFatalError) throw err;
        consecutiveFailures += 1;
        if (consecutiveFailures === REPORT_AFTER_CONSECUTIVE_FAILURES) {
          logger.captureError(new Error(`Point SSE reconnect failing: attempts=${consecutiveFailures} lastError=${err instanceof Error ? err.message : String(err)}`));
        }
        if (err instanceof PointSseRejectedError) consecutiveRejections += 1;
        if (consecutiveRejections >= MAX_RECONNECT_ATTEMPTS) {
          // 반환이 아니라 throw 여야 라이브러리가 재연결 타이머를 걸지 않는다.
          throw new PointSseFatalError(`Point SSE giving up: rejections=${consecutiveRejections}`);
        }
        return nextReconnectDelay();
      }
    }).catch(() => {
      // fatal 종료로 인한 reject. 로그인 유도는 전역 에러 핸들러 소관이라 여기선 삼킨다.
    });

    // play 밖에서는 지금까지처럼 즉시 붙는다.
    if (!isPlayRoute) {
      connect();
      return () => {
        abortController.abort();
      };
    }
    const startTimer = setTimeout(connect, PLAY_SSE_START_DELAY_MS);
    return () => {
      clearTimeout(startTimer);
      abortController.abort();
    };
  }, [queryClient, quizUserId, isStudent, hasJoinedClassroom, apiToken, isPlayRoute]);
}